import { Checkbox, Currency, Form, Grid, Input } from '@mairistem/composants';
import { ImputationSearchInput, Sens } from '@mairistem/comptabilite';
import { Combobox } from '@mairistem/finances-utils';
import type { DropdownOption } from '@mairistem/liste';
import { isNil, isString, map } from 'lodash';
import React from 'react';
import type { ConditionVersement } from '@/src/interfaces/conditionVersement';
import {
	handleMontantChangeImpact,
	handlePourcentageChangeImpact,
	handlePourcentageCumuleChangeImpact,
} from '@/src/utils/conditionVersement';

const currentYear = new Date().getFullYear();
const CHAMP_MONTANT = 0;
const CHAMP_POURCENTAGE = 1;
const CHAMP_POURCENTAGE_CUMULE = 2;

interface EtapeCaracteristiqueProps {
	budgetOptions: DropdownOption[];
	conditionVersement: ConditionVersement;
	montantPrecedentesConditions: number;
	onLibelleDatalist: () => string[];
	montantSubvention: number;
	onChange: (condition: any, isDatabaseChanges?: boolean) => void;
}

const EtapeCaracteristique = ({
	conditionVersement = null,
	montantPrecedentesConditions = null,
	onLibelleDatalist = null,
	montantSubvention = 0,
	onChange = null,
	budgetOptions = null,
}: EtapeCaracteristiqueProps) => {
	const budgetTypeOptions: DropdownOption[] = [
		{
			key: 'BP',
			value: 'BP',
			text: 'Budget primitif',
			'data-testid': 'budgetPrimitifOption',
		},
		{
			key: 'BS',
			value: 'BS',
			text: 'Budget supplémentaire',
			'data-testid': 'budgetSuplementaireOption',
		},
	];

	const updateMontants = React.useCallback(
		(baseChanges = null) => {
			if (
				montantSubvention !== 0 &&
				conditionVersement?.montant !== null &&
				montantPrecedentesConditions != null
			) {
				const champCalcul =
					baseChanges?.champCalcul ||
					conditionVersement?.champCalcul ||
					CHAMP_MONTANT;
				let changes: any = null;
				switch (champCalcul) {
					case CHAMP_MONTANT:
						changes = handleMontantChangeImpact(
							baseChanges?.montant ?? conditionVersement?.montant,
							montantSubvention,
							montantPrecedentesConditions,
						);
						break;
					case CHAMP_POURCENTAGE:
						changes = handlePourcentageChangeImpact(
							baseChanges?.pourcentageSubvention ??
								conditionVersement?.pourcentageSubvention,
							montantSubvention,
							montantPrecedentesConditions,
						);
						break;
					case CHAMP_POURCENTAGE_CUMULE:
						changes = handlePourcentageCumuleChangeImpact(
							baseChanges?.pourcentageCumuleSubvention ??
								conditionVersement?.pourcentageCumuleSubvention,
							montantSubvention,
							montantPrecedentesConditions,
						);

						break;
				}

				onChange(
					{
						...changes,
						...baseChanges,
						imputationsVersements: [
							{
								...(conditionVersement?.imputationsVersements?.[0] ?? {}),
								montant: changes.montant,
							},
						],
					},
					changes?.montant !== conditionVersement.montant,
				);
			}
		},
		[
			conditionVersement,
			montantPrecedentesConditions,
			montantSubvention,
			onChange,
		],
	);

	const handleChangePourcentageSubvention = (_e, value) => {
		updateMontants({
			pourcentageSubvention: value,
			champCalcul: CHAMP_POURCENTAGE,
		});
	};

	const handleChangePourcentageCumuleSubvention = React.useCallback(
		(_e, value) => {
			if (conditionVersement?.pourcentageCumuleSubvention !== value) {
				updateMontants({
					pourcentageCumuleSubvention: value,
					champCalcul: CHAMP_POURCENTAGE_CUMULE,
				});
			}
		},
		[conditionVersement?.pourcentageCumuleSubvention, updateMontants],
	);

	React.useEffect(() => {
		if (
			isNil(conditionVersement?.pourcentageCumuleSubvention) &&
			isNil(conditionVersement?.pourcentageSubvention)
		) {
			// si on a pas déjà pré-rentré des montants
			updateMontants();
		}
	}, [
		conditionVersement?.pourcentageCumuleSubvention,
		conditionVersement?.pourcentageSubvention,
		updateMontants,
	]);

	const handleChangeSelect = (_e, { name, value }) => {
		onChange({
			[name]: value,
		});
	};

	const handleChangeInput = ({ target: { name, value } }) => {
		onChange({ [name]: value });
	};

	const anneeList = React.useMemo(() => {
		const result = [];
		for (let i = currentYear, max = currentYear + 3; i < max; i++) {
			result.push(i);
		}
		return result;
	}, []);

	const resetImputation = () => {
		const deletedImputationsVersements = [];
		if (
			conditionVersement.imputationsVersements?.length > 0 &&
			!isString(conditionVersement.imputationsVersements[0]?.identifiant)
		) {
			deletedImputationsVersements.push(
				conditionVersement.imputationsVersements[0]?.identifiant,
			);
		}

		onChange({
			imputationsVersements: [],
			...(deletedImputationsVersements?.length > 0 && {
				deletedImputationsVersements,
			}),
		});
	};

	const handleChangeBudget = (e, data) => {
		resetImputation();
		handleChangeSelect(e, data);
	};

	const handleChangeExercice = ({ target: { valueAsNumber } }) => {
		onChange({ annee: Number.isNaN(valueAsNumber) ? null : valueAsNumber });
		resetImputation();
	};

	const handleImputationChange = (imputation) => {
		if (imputation === null) {
			resetImputation();
			return;
		}

		const imputationVersement =
			conditionVersement.imputationsVersements[0] ?? null;
		const imputationExtra = {
			section: imputation?.chapitre?.section,
			codeChapitre: imputation?.chapitre?.code,
			codeArticle: imputation?.article?.code,
			codeOperation: imputation?.operation?.code,
			codeFonction: imputation?.fonction?.code,
			codeVentilation: imputation?.ventilation?.code,
			codeAnalytique: imputation?.analytique?.code,
			natureBudgetaire: imputation?.chapitre?.natureBudgetaire,
		};

		// on est sur la meme annee alors on va garder seulement l'identifiant de l'imputation
		if (imputation?.exercice?.annee === conditionVersement.annee) {
			onChange({
				imputationsVersements: [
					{
						...imputationVersement,
						libelle: imputation.libelle,
						identifiantImputation: imputation.identifiant,
						montant: conditionVersement.montant,
						chapitre: imputation?.chapitre,
						sens: imputation?.sens,
						...imputationExtra,
					},
				],
			});
		} else {
			onChange({
				imputationsVersements: [
					{
						...imputationVersement,
						libelle: imputation.libelle,
						chapitre: imputation?.chapitre,
						montant: conditionVersement.montant,
						sens: imputation?.sens,
						identifiantImputation: null,
						...imputationExtra,
					},
				],
			});
		}
	};

	const handleChangeMontant = (_e, value) => {
		updateMontants({ montant: value, champCalcul: CHAMP_MONTANT });
	};

	const getDefaultImputation = () => {
		const imputation = conditionVersement?.imputationsVersements?.[0];
		if (imputation) {
			return {
				libelle: imputation.libelle,
				sens: 'R',
				chapitre: {
					code: imputation.codeChapitre,
					section: imputation.section,
				},
			};
		}

		return null;
	};

	const handleCheckboxChange = (_e, { name, checked }) => {
		onChange({ [name]: checked });
	};

	const imputationDisabled =
		isNil(conditionVersement?.identifiantEntiteCompta) ||
		isNil(conditionVersement?.annee);

	return (
		<Grid>
			<Grid.Row>
				<Grid.Column width={16}>
					<Input
						key="libelleCondition"
						name="libelle"
						label="Intitulé"
						value={conditionVersement?.libelle}
						placeholder="Choissez une proposition ou saisissez librement un intitulé"
						onChange={handleChangeInput}
						fluid
						required
						list="libelleCondition"
					>
						<input data-testid="libelleConditionVersement" autoComplete="off" />
					</Input>
					<datalist id="libelleCondition">
						{map(onLibelleDatalist(), (libelle) => (
							<option>{libelle}</option>
						))}
					</datalist>
				</Grid.Column>
			</Grid.Row>
			<Grid.Row>
				<Grid.Column width={6}>
					<Currency
						key="montant"
						name="montant"
						required
						label="Montant du versement"
						placeholder="Montant du versement"
						value={conditionVersement?.montant}
						className="flex-1"
						debounced={false}
						onChange={handleChangeMontant}
						fluid
					>
						<input data-testid="montantVersement" />
					</Currency>
				</Grid.Column>

				<Grid.Column width={5}>
					<Currency
						key="pourcentageSubvention"
						name="pourcentageSubvention"
						label="Pourcentage de la subvention"
						placeholder="Pourcentage de la subvention"
						fluid
						percent
						value={conditionVersement?.pourcentageSubvention}
						onChange={handleChangePourcentageSubvention}
						debounced={false}
						percentMin={0}
						percentMax={100}
						description="Le taux doit être compris entre 0 et 100."
					>
						<input data-testid="pourcentageSubvention" />
					</Currency>
				</Grid.Column>

				<Grid.Column width={5}>
					<Currency
						key="pourcentageCumuleSubvention"
						name="pourcentageCumuleSubvention"
						label="Pourcentage cumulé de la subvention"
						placeholder="Pourcentage cumulé de la subvention"
						onChange={handleChangePourcentageCumuleSubvention}
						debounced={false}
						value={conditionVersement?.pourcentageCumuleSubvention}
						fluid
						percent
						percentMin={0}
						percentMax={100}
						description="Le taux doit être compris entre 0 et 100."
					>
						<input data-testid="pourcentageCumuleSubvention" />
					</Currency>
				</Grid.Column>
			</Grid.Row>
			<Grid.Row>
				<Grid.Column width={16}>
					<Form.Field
						control={Combobox}
						data-testid="budgetVersementDropdown"
						// @ts-expect-error
						fluid
						label="Budget"
						name="identifiantEntiteCompta"
						onChange={handleChangeBudget}
						options={budgetOptions}
						placeholder="Budget"
						required
						selection
						value={conditionVersement?.identifiantEntiteCompta}
					/>
				</Grid.Column>
			</Grid.Row>
			<Grid.Row>
				<Grid.Column width={4}>
					<Input
						key="annee"
						name="annee"
						label="Exercice"
						value={conditionVersement?.annee}
						placeholder="Exercice"
						onChange={handleChangeExercice}
						type="number"
						required
						fluid
						list="exerciceList"
					>
						<input data-testid="exerciceCondition" autoComplete="off" />
					</Input>
					<datalist id="exerciceList">
						{anneeList.map((exercice) => (
							<option key={exercice}>{exercice}</option>
						))}
					</datalist>
				</Grid.Column>
				<Grid.Column width={12}>
					<Form.Field
						control={Combobox}
						data-testid="typeBudgetConditionDropdown"
						// @ts-expect-error
						fluid
						label="Inscription des prévisions au"
						name="typeBudget"
						onChange={handleChangeSelect}
						options={budgetTypeOptions}
						placeholder="Budget"
						selection
						value={conditionVersement?.typeBudget}
					/>
				</Grid.Column>
			</Grid.Row>
			<Grid.Row>
				<Grid.Column width={16}>
					<Form.Field>
						{/** biome-ignore lint/a11y/noLabelWithoutControl: Label semantic */}
						<label>Imputation</label>
						<ImputationSearchInput
							placeholder={
								imputationDisabled
									? 'Veuillez selectionner un budget et un exercice.'
									: 'Commencez à saisir un article'
							}
							label="Imputation"
							name="imputation"
							onChange={handleImputationChange}
							disabled={imputationDisabled}
							identifiantEntite={conditionVersement?.identifiantEntiteCompta}
							annee={conditionVersement?.annee}
							sens={Sens.RECETTE}
							perPage={10}
							defaultValue={getDefaultImputation()}
							dataTestid="imputationSearchInputCondition"
							searchComplement={{
								withAnalytique: true,
								withArticle: true,
								withChapitre: true,
								withFonction: true,
								withOperation: true,
								withVentilation: true,
							}}
						/>
					</Form.Field>
				</Grid.Column>
			</Grid.Row>
			<Grid.Row>
				<Grid.Column width={16}>
					<Checkbox
						name="doitEngager"
						className="visibility"
						label="Ce versement doit faire l'objet d'un engagement de recette"
						checked={conditionVersement?.doitEngager}
						onChange={handleCheckboxChange}
						data-testid="doitEngagerConditionCheckbox"
					/>
				</Grid.Column>
			</Grid.Row>
		</Grid>
	);
};

export default EtapeCaracteristique;
